/**
 * tiscali.it — TEK sayfa (2026-09-16 operatör isteği): stok, kimlik doğrulama,
 * site başına satışlar ve mailbox başına "hangi siteler için satıldı" detayı,
 * import ve yenile — hepsi burada. (Alias-model şablonu bilinçli olarak
 * kullanılmaz: tiscaliit'da alias yok, satış hakkı (mailbox, site) bazındadır.)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCan } from '../../session/can.js';
import { BulkImportModal } from '../mailcom/BulkImportModal.js';
import {
  availableShare,
  fetchTiscaliitSiteUsage,
  mailboxStateLabel,
  type TiscaliitMailboxRow,
  type TiscaliitSiteUsage,
  type TiscaliitSiteUsageRow,
} from './siteUsage.js';

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ok'; readonly data: TiscaliitSiteUsage }
  | { readonly kind: 'error'; readonly message: string };

type MailboxFilter = 'all' | 'sold' | 'pending' | 'quarantined';

function fmt(ts: string | null | undefined): string {
  return ts ? new Date(ts).toLocaleString('tr-TR') : '—';
}

export function TiscaliitPage(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const canManage = useCan('provider:manage_inventory');
  const [showImport, setShowImport] = useState(false);
  const [filter, setFilter] = useState<MailboxFilter>('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async (signal?: AbortSignal) => {
    setState({ kind: 'loading' });
    const res = await fetchTiscaliitSiteUsage(signal);
    if (res.kind === 'ok') {
      setState({ kind: 'ok', data: res.data });
      return;
    }
    if (res.kind === 'auth_required') {
      setState({ kind: 'error', message: 'Oturum gerekli — lütfen admin girişi yapın.' });
      return;
    }
    if (res.kind === 'forbidden') {
      setState({ kind: 'error', message: 'Yetki yok: provider:inspect izni gerekiyor.' });
      return;
    }
    if (res.kind === 'network_error') {
      setState({ kind: 'error', message: 'Bağlantı hatası: ' + res.message });
      return;
    }
    setState({ kind: 'error', message: 'İstek başarısız (' + res.status + ': ' + res.reason + ').' });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const mailboxes = useMemo<readonly TiscaliitMailboxRow[]>(() => {
    if (state.kind !== 'ok') return [];
    const all = state.data.mailboxes ?? [];
    const q = search.trim().toLowerCase();
    return all.filter((m) => {
      if (q && !m.address.toLowerCase().includes(q)) return false;
      if (filter === 'sold') return m.burnedSites.length + m.servedSites.length > 0;
      if (filter === 'pending') return m.accountStatus === 'active' && !m.verified;
      if (filter === 'quarantined') return m.accountStatus === 'login_failed';
      return true;
    });
  }, [state, filter, search]);

  return (
    <section className="tiscaliit-dashboard" data-testid="tiscaliit-dashboard">
      <header className="tiscaliit-head">
        <div>
          <h1>tiscali.it — stok, kimlik ve site kullanımı</h1>
          <p>
            Her satır bir mailbox'tır (alias üretilmez). Satış hakkı{' '}
            <strong>(mailbox, site)</strong> bazındadır: posta gelmezse yalnız o
            müşteriye kapanır ve başka müşteriye satılabilir; posta gelirse çift
            herkese yanar. Bir mailbox, kimliği doğrulanmadan satılmaz.
          </p>
        </div>
        <div className="tiscaliit-head-actions">
          {state.kind === 'ok' && (
            <span className="badge badge-neutral">
              {state.data.accounts} hesap · {state.data.units.total} unit
            </span>
          )}
          {canManage ? (
            <button
              type="button"
              className="btn"
              onClick={() => setShowImport(true)}
              data-testid="tiscaliit-import"
              title="email:şifre satırları — mailbox'ın kendisi unit olarak eklenir; şifre ':' içerebilir"
            >
              Import / TXT
            </button>
          ) : null}
          <button type="button" className="btn" onClick={() => void load()} data-testid="tiscaliit-refresh">
            Yenile
          </button>
        </div>
      </header>
      {canManage ? (
        <BulkImportModal
          providerId="tiscaliit"
          providerLabel="tiscali.it"
          open={showImport}
          onClose={() => setShowImport(false)}
          onCompleted={() => void load()}
        />
      ) : null}

      {state.kind === 'loading' && (
        <div className="card">
          <div className="card-body" data-testid="tiscaliit-loading">Yükleniyor…</div>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="card">
          <div className="card-body" role="alert" data-testid="tiscaliit-error">
            <span className="badge badge-danger" style={{ marginRight: 8 }}>Hata</span>
            {state.message}
          </div>
        </div>
      )}

      {state.kind === 'ok' && (
        <>
          <div className="kpi-grid" style={{ marginBottom: 16 }}>
              <div className="stat" data-testid="kpi-total">
                <div className="stat-label">Toplam stok</div>
                <div className="stat-value">{state.data.units.total}</div>
                <div className="stat-meta">mailbox (her biri bir unit)</div>
              </div>
              <div className="stat" data-testid="kpi-available">
                <div className="stat-label">Satılabilir</div>
                <div className="stat-value">{state.data.units.available}</div>
                <div className="stat-meta up">stokun %{availableShare(state.data.units)}</div>
              </div>
              <div className="stat" data-testid="kpi-leased">
                <div className="stat-label">Kiralamada</div>
                <div className="stat-value">{state.data.units.leased}</div>
                <div className="stat-meta">şu an müşteride</div>
              </div>
              <div className="stat" data-testid="kpi-verified">
                <div className="stat-label">Kimlik doğrulandı</div>
                <div className="stat-value">{state.data.probe?.verified ?? '—'}</div>
                <div className="stat-meta">
                  {state.data.probe?.pending ?? 0} bekliyor · {state.data.probe?.quarantined ?? 0} karantina
                </div>
              </div>
              <div className="stat" data-testid="kpi-burned-pairs">
                <div className="stat-label">Yanmış hak</div>
                <div className="stat-value">{state.data.units.burnedPairs}</div>
                <div className="stat-meta">(mailbox, site) çifti — kalıcı</div>
              </div>
              <div className="stat" data-testid="kpi-burned-mailboxes">
                <div className="stat-label">Yanmış mailbox</div>
                <div className="stat-value">{state.data.units.mailboxesWithBurns}</div>
                <div className="stat-meta">
                  {state.data.units.total - state.data.units.mailboxesWithBurns} mailbox hiç yanmadı
                </div>
              </div>
              <div className="stat" data-testid="kpi-accounts">
                <div className="stat-label">Hesap</div>
                <div className="stat-value">{state.data.accounts}</div>
                <div className="stat-meta">import edilmiş tiscaliit hesabı</div>
              </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div>
                <div className="card-title">Site başına satış</div>
                <div className="card-subtitle">
                  Hangi site için kaç mailbox satıldı; posta gelince çift kalıcı yanar, gelmezse yalnız o müşteriye kapanır
                </div>
              </div>
              <div className="card-header-actions">
                <span className="badge badge-neutral">{state.data.sites.length} site</span>
              </div>
            </div>
            {state.data.sites.length === 0 ? (
              <div className="card-body" data-testid="tiscaliit-no-sites">
                <span className="badge badge-success" style={{ marginRight: 8 }}>Temiz</span>
                Henüz hiçbir site için satış yok — tüm stok her site için kullanılabilir.
              </div>
            ) : (
              <div className="tbl-wrap" style={{ overflowX: 'auto' }}>
                <table className="tbl" data-testid="tiscaliit-site-table">
                  <thead>
                    <tr>
                      <th>Site</th>
                      <th>Satılan mailbox</th>
                      <th>Yanmış (posta geldi)</th>
                      <th>Satıldı, posta gelmedi</th>
                      <th style={{ minWidth: 200 }}>Stok payı (yanma)</th>
                      <th>Son satış</th>
                      <th>Son yanma</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.sites.map((row) => (
                      <tr key={row.site} data-testid={'site-row-' + row.site}>
                        <td className="t-mono" data-label="Site">{row.site}</td>
                        <td data-label="Satılan mailbox" data-testid={'site-sold-' + row.site}>{row.soldMailboxes ?? row.burnedMailboxes + row.servedMailboxes}</td>
                        <td data-label="Yanmış (posta geldi)">{row.burnedMailboxes}</td>
                        <td data-label="Satıldı, posta gelmedi">{row.servedMailboxes}</td>
                        <td className="tiscaliit-share-td" data-label="Stok payı (yanma)">
                          <div className="tiscaliit-share">
                            <span className="tiscaliit-bar" aria-hidden="true">
                              <span
                                className={'tiscaliit-bar-fill' + shareClass(row)}
                                style={{ width: Math.min(100, row.shareOfStock) + '%' }}
                              />
                            </span>
                            <span className="badge badge-neutral" data-testid={'site-share-' + row.site}>
                              %{row.shareOfStock}
                            </span>
                          </div>
                        </td>
                        <td className="t-small t-tertiary" data-label="Son satış">{fmt(row.lastSoldAt)}</td>
                        <td className="t-small t-tertiary" data-label="Son yanma">{fmt(row.lastBurnedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-header">
              <div>
                <div className="card-title">Mailbox'lar</div>
                <div className="card-subtitle">
                  Her mailbox'ın kimlik durumu, son görülme ve hangi siteler için satıldığı
                </div>
              </div>
              <div className="card-header-actions tiscaliit-controls">
                <input
                  className="input"
                  placeholder="adres ara"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="tiscaliit-mailbox-search"
                  style={{ minWidth: 180 }}
                />
                <select
                  className="input"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as MailboxFilter)}
                  data-testid="tiscaliit-mailbox-filter"
                >
                  <option value="all">Hepsi</option>
                  <option value="sold">Satılmış</option>
                  <option value="pending">Doğrulama bekleyen</option>
                  <option value="quarantined">Karantina</option>
                </select>
                <span className="badge badge-neutral">{mailboxes.length} / {(state.data.mailboxes ?? []).length}</span>
              </div>
            </div>
            {mailboxes.length === 0 ? (
              <div className="card-body" data-testid="tiscaliit-no-mailboxes">
                {(state.data.mailboxes ?? []).length === 0
                  ? 'Henüz mailbox yok — Import / TXT ile ekleyin.'
                  : 'Filtreye uyan mailbox yok.'}
              </div>
            ) : (
              <div className="tbl-wrap" style={{ overflowX: 'auto' }}>
                <table className="tbl" data-testid="tiscaliit-mailbox-table">
                  <thead>
                    <tr>
                      <th>Mailbox</th>
                      <th>Unit</th>
                      <th>Kimlik</th>
                      <th>Son görülme</th>
                      <th>Satıldığı siteler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mailboxes.map((m) => {
                      const st = mailboxStateLabel(m);
                      return (
                        <tr key={m.address} data-testid={'mailbox-row-' + m.address}>
                          <td className="t-mono" data-label="Mailbox">{m.address}</td>
                          <td data-label="Unit"><span className="badge badge-neutral">{m.unitStatus}</span></td>
                          <td className="tiscaliit-state-td" data-label="Kimlik">
                            <span className="tiscaliit-state-cell">
                              <span className={'badge badge-' + st.tone} data-testid={'mailbox-state-' + m.address}>{st.text}</span>
                              {m.consecutiveErrors > 0 ? (
                                <span className="t-small t-tertiary">
                                  {m.consecutiveErrors} hata{m.cooldownUntil ? ' · tekrar ' + fmt(m.cooldownUntil) : ''}
                                </span>
                              ) : null}
                            </span>
                          </td>
                          <td className="t-small t-tertiary" data-label="Son görülme">{fmt(m.lastSeenAt)}</td>
                          <td className="tiscaliit-sites-td" data-label="Satıldığı siteler" data-testid={'mailbox-sites-' + m.address}>
                            {m.burnedSites.length + m.servedSites.length === 0 ? (
                              <span className="t-tertiary">—</span>
                            ) : (
                              <span className="tiscaliit-sites-cell">
                                {m.burnedSites.map((s) => (
                                  <span key={'b-' + s} className="badge badge-danger" title="posta geldi — herkese kapalı">
                                    {s}
                                  </span>
                                ))}
                                {m.servedSites.map((s) => (
                                  <span key={'s-' + s} className="badge badge-warn" title="satıldı, posta gelmedi — yalnız o müşteriye kapalı">
                                    {s}
                                  </span>
                                ))}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-body">
              <div className="card-title" style={{ marginBottom: 6 }}>Kapasite</div>
              <p className="tiscaliit-footnote" data-testid="tiscaliit-capacity-note">
                {state.data.units.total} mailbox × sınırsız site hakkı. Şu an{' '}
                <strong>{state.data.units.available}</strong> mailbox satılabilir (%{availableShare(state.data.units)});
                yanmış çiftler yalnızca kendi sitesi için kapalı, mailbox'ın kendisi kapanmaz.
                Import edilen bir mailbox, kimlik doğrulaması (dakikada 10 hesap) geçince satışa açılır.
              </p>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

/** Bar rengi: payı büyüdükçe dikkat çeksin (yeşil → sarı → kırmızı). */
function shareClass(row: TiscaliitSiteUsageRow): string {
  if (row.shareOfStock >= 50) return ' is-high';
  if (row.shareOfStock >= 20) return ' is-mid';
  return '';
}
